import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Combobox, ComboboxItem } from '@node-space/ui-components'
import { useRewardsSheetContext } from 'pages/Rewards/context/RewardsSheetProvider'

interface RewardsCurrencySelectProps {
  selectedCurrency?: string
  disabled?: boolean
  filterByBalance?: 'available' | 'blocked'
  setMaxAmount?: (value: string) => void
}

export const RewardsCurrencySelect = ({
  selectedCurrency,
  disabled = false,
  filterByBalance,
  setMaxAmount,
}: RewardsCurrencySelectProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const { userPortfolios } = useRewardsSheetContext()

  const currencyOptions = useMemo<ComboboxItem[]>(() => {
    if (!userPortfolios) return []

    let filteredPortfolios = userPortfolios

    if (filterByBalance) {
      filteredPortfolios = userPortfolios.filter(portfolio => {
        const balance = Number(portfolio?.balance?.[filterByBalance]) || 0
        return balance > 0
      })
    }

    return filteredPortfolios.map(portfolio => ({
      value: portfolio.currency,
      leftText: portfolio.currency,
      LeftElement: <Avatar name={portfolio.currency} size="small" />,
    }))
  }, [userPortfolios, filterByBalance])

  const handleSetMaxAmount = (value: string) => {
    if (setMaxAmount) {
      const portfolio = userPortfolios.find(portfolio => portfolio.currency === value)
      const portfolioBalance = portfolio?.balance?.[filterByBalance || 'available']

      setMaxAmount(portfolioBalance || '0')
    }
  }

  // Set max amount in case where currency is selected
  useEffect(() => {
    if (disabled && selectedCurrency) {
      handleSetMaxAmount(selectedCurrency)
    }
  }, [currencyOptions])

  return (
    <Controller
      control={control}
      name="currency"
      render={({ field: { value, ...field }, fieldState: { error } }) => (
        <Combobox
          {...field}
          onChange={value => {
            handleSetMaxAmount(value)
            field.onChange(value)
          }}
          disabled={disabled}
          id="currency"
          label={t('currency')}
          selectPlaceholder={t('select')}
          items={currencyOptions}
          defaultValue={value}
          errorText={error?.message}
          testId="currency-combobox"
        />
      )}
    />
  )
}
